import React, {useState} from 'react';
import {useSelector} from '../../reduxHooks';
import {Alert, Button, Card, TextField} from '@betareader/design-system';

const templateOptions = [
  {value: 'weekly-digest', label: 'Weekly Digest'},
  {value: 'daily-digest', label: 'Daily Digest'},
];

const SuperAdmin: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<{html: string; subject: string} | null>(null);
  const [message, setMessage] = useState<{type: 'success' | 'error'; content: string} | null>(null);
  const [email, setEmail] = useState('jonas.b.frid@gmail.com');
  const [templateType, setTemplateType] = useState('weekly-digest');
  const userProfile = useSelector((state: any) => state.user.userProfile);

  const handlePreview = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/global/test-email-preview?template=${templateType}`,
        {method: 'GET', headers: {'Content-Type': 'application/json'}}
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      setPreview(await response.json());
    } catch (error) {
      console.error('Preview failed:', error);
      setMessage({type: 'error', content: `Failed to generate preview: ${error instanceof Error ? error.message : String(error)}`});
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/global/send-test-email`,
        {
          method: 'POST', headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({email, templateType}),
        }
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      await response.json();
      setMessage({type: 'success', content: `Test email sent successfully to ${email}!`});
    } catch (error) {
      console.error('Send test email failed:', error);
      setMessage({type: 'error', content: `Failed to send test email: ${error instanceof Error ? error.message : String(error)}`});
    } finally {
      setLoading(false);
    }
  };

  if (!userProfile) return <p role='status'>Loading user profile...</p>;

  return (
    <main style={{maxWidth: 800, margin: '0 auto', padding: 20}}>
      <h1>📧 Email Testing Interface</h1>
      <Alert>
        <strong>Email Template Testing</strong>
        <p>Use this interface to preview and send test emails with the latest templates.</p>
      </Alert>
      {message && (
        <Alert variant={message.type}>{message.content}</Alert>
      )}
      <Card style={{padding: '1.25rem', margin: '20px 0'}}>
        <h2>Test Email Configuration</h2>
        <div style={{display: 'grid', gap: '1rem'}}>
          <label className='br-field' htmlFor='test-email-template'>
            <span className='br-field__label'>Template Type</span>
            <select id='test-email-template' className='br-field__input' value={templateType}
              onChange={event => setTemplateType(event.target.value)}>
              {templateOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <TextField id='test-email-address' label='Test Email Address' type='email'
            placeholder='Enter email address' value={email}
            onChange={event => setEmail(event.target.value)} />
          <div style={{display: 'flex', flexWrap: 'wrap', gap: '.75rem'}}>
            <Button variant='secondary' onClick={handlePreview} disabled={loading || !templateType}
              aria-busy={loading}>Generate Preview</Button>
            <Button onClick={handleSendTestEmail} disabled={loading || !email || !templateType}
              aria-busy={loading}>Send Test Email</Button>
          </div>
        </div>
      </Card>
      {preview && (
        <Card style={{padding: '1.25rem'}}>
          <h2>Email Preview: {preview.subject}</h2>
          {/* Server-generated templates contain tables and inline styles needed for preview. */}
          <div dangerouslySetInnerHTML={{__html: preview.html}}
            style={{border: '1px solid #ddd', padding: 10, maxHeight: 400,
              overflow: 'auto', backgroundColor: '#f9f9f9'}} />
        </Card>
      )}
    </main>
  );
};

export default SuperAdmin;
